import { Box, FormControlLabel, Switch } from "@mui/material";
import TextField from "@mui/material/TextField";
import { Contact, useUpdateContactDeleteMutation } from "@repo/server-api";
import { ContactExtended } from "@repo/server-api/types";
import { useNotification } from '@repo/shared/components';
import { useTranslation } from "next-i18next";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import * as Yup from "yup";

import { setContactValues } from "@repo/legacy/components/helper/Index";


type Exposed = {
	validate: () => Promise<Record<string, boolean | object | string>>;
};

const CompanyIdentification = forwardRef(
	(
		props: Readonly<{
			contact: Contact | null;
			setContact: (value: Contact) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			isDialog?: boolean;
		}>,
		ref: React.Ref<Exposed>,
	) => {
		const { t } = useTranslation();

		const { showNotification } = useNotification();

		const info = useMemo<Contact>(
			() => ({
				...setContactValues(props.contact),
			}),

			[props.contact],
		);

		const [companyNameLocal, setCompanyNameLocal] = useState(info.companyName ?? "");
		const [contactNumberLocal, setContactNumberLocal] = useState(info.contactNumber ?? "");

		const companyFocusedRef = useRef(false);
		const numberFocusedRef = useRef(false);

		const [deleteContact] = useUpdateContactDeleteMutation();

		useEffect(() => {
			if (!companyFocusedRef.current) setCompanyNameLocal(info.companyName ?? "");
		}, [info.companyName]);

		React.useEffect(() => {
			if (!numberFocusedRef.current) setContactNumberLocal(info.contactNumber ?? "");
		}, [info.contactNumber]);

		const validationSchema = Yup.object().shape({
			companyName: Yup.string().required(t("company.name-required", { ns: "validation" })),
		});

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(
					{ companyName: companyNameLocal, contactNumber: contactNumberLocal },
					{ abortEarly: false },
				);
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [companyNameLocal, contactNumberLocal, props, t]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const setInContact = (patch: Partial<ContactExtended>) => {
			if (!props.contact) return;
			props.setContact({ ...props.contact, ...patch } as ContactExtended);
		};

		return (
			<div>
				{!props.isDialog ? (
					<Box className="fullCenter">
						<FormControlLabel
							control={
								<Switch
									checked={!info.isDeleted}
									onChange={(event) => {
										const v = !event.target.checked;
										deleteContact({
											variables: { id: info.id, isDeleted: v },
										}).then(() => {
											if (v) showNotification(t("message.contact-inactivated"), "success");
											else showNotification(t("message.contact-restored"), "success");
											setInContact({ isDeleted: v });
										});
									}}
								/>
							}
							label={t("Active")}
						/>
					</Box>
				) : (
					""
				)}
				<Box className="fullCenter" sx={{ mt: 3 }}>
					<TextField
						onChange={(event) => {
							const v = event.target.value ?? "";
							setCompanyNameLocal(v);
							setInContact({ companyName: v, displayName: v });
						}}
						onFocus={() => {
							companyFocusedRef.current = true;
						}}
						onBlur={() => {
							companyFocusedRef.current = false;
							setCompanyNameLocal(setContactValues(props.contact).companyName ?? "");
						}}
						error={!!props.errors?.companyName}
						helperText={props.errors?.companyName as string}
						sx={{ float: "left", width: "100%", marginBottom: "10px" }}
						value={companyNameLocal}
						label={t("company-name")}
						variant="outlined"
					/>
				</Box>

				{props.isDialog ? (
					""
				) : (
					<Box className="fullCenter">
						<TextField
							onChange={(event) => {
								const v = event.target.value ?? "";
								setContactNumberLocal(v);
								setInContact({ contactNumber: v });
							}}
							onFocus={() => {
								numberFocusedRef.current = true;
							}}
							onBlur={() => {
								numberFocusedRef.current = false;
								setContactNumberLocal(setContactValues(props.contact).contactNumber ?? "");
							}}
							error={!!props.errors?.contactNumber}
							helperText={props.errors?.contactNumber as string}
							value={contactNumberLocal}
							label={t("contact-number")}
							variant="outlined"
							sx={{ float: "left", width: "100%", marginTop: "10px" }}
						/>
					</Box>
				)}
			</div>
		);
	},
);

CompanyIdentification.displayName = "CompanyIdentification";
export default CompanyIdentification;
